import React from 'react';
import {
    Target,
    Users,
    Image,
    GitBranch,
    GripVertical,
    Info,
    Flame,
    RefreshCw
} from 'lucide-react';

/**
 * NodePanel Component
 * Draggable node templates panel for adding nodes to canvas
 */
const NodePanel = ({ onAddNode }) => {
    const nodeTypes = [
        {
            type: 'campaign',
            label: 'Campaign',
            icon: Target,
            color: 'var(--color-primary)',
            description: 'Top-level campaign with objective and budget'
        },
        {
            type: 'adset',
            label: 'Ad Set',
            icon: Users,
            color: 'var(--color-info)',
            description: 'Targeting, audience, and schedule settings'
        },
        {
            type: 'ad',
            label: 'Ad',
            icon: Image,
            color: 'var(--color-success)',
            description: 'Creative with headline, copy, and CTA'
        },
        {
            type: 'abtest',
            label: 'A/B Test',
            icon: GitBranch,
            color: 'var(--color-warning)',
            description: 'Split test container for ad variants'
        },
        {
            type: 'segment',
            label: 'Audience Segment',
            icon: Flame,
            color: 'var(--color-accent, #9333ea)',
            description: 'High-intent audience for targeting'
        },
        {
            type: 'retargetingPool',
            label: 'Retargeting Pool',
            icon: RefreshCw,
            color: 'var(--color-warning)',
            description: 'Auto-generated retargeting audience'
        }
    ];

    const handleDragStart = (e, nodeType) => {
        e.dataTransfer.setData('application/reactflow', nodeType);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleClick = (nodeType) => {
        if (onAddNode) {
            onAddNode(nodeType);
        }
    };

    return (
        <div style={{
            width: '240px',
            background: 'var(--color-surface)',
            borderRight: '1px solid var(--color-border)',
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
        }}>
            {/* Header */}
            <div style={{
                padding: 'var(--space-4)',
                borderBottom: '1px solid var(--color-border)'
            }}>
                <h3 style={{
                    margin: 0,
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: 'var(--color-text-secondary)'
                }}>
                    Add Nodes
                </h3>
                <p style={{
                    margin: 'var(--space-2) 0 0',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-tertiary)'
                }}>
                    Drag onto canvas or click to add
                </p>
            </div>

            {/* Node Templates */}
            <div style={{
                flex: 1,
                padding: 'var(--space-3)',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-2)'
            }}>
                {nodeTypes.map(({ type, label, icon: Icon, color, description }) => (
                    <div
                        key={type}
                        draggable
                        onDragStart={(e) => handleDragStart(e, type)}
                        onClick={() => handleClick(type)}
                        style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 'var(--space-3)',
                            padding: 'var(--space-3)',
                            background: 'var(--color-bg-secondary)',
                            border: '1px solid var(--color-border)',
                            borderRadius: 'var(--radius-md)',
                            cursor: 'grab',
                            transition: 'all 0.15s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = color;
                            e.currentTarget.style.background = 'var(--color-bg-tertiary)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = 'var(--color-border)';
                            e.currentTarget.style.background = 'var(--color-bg-secondary)';
                        }}
                    >
                        {/* Drag Handle */}
                        <GripVertical
                            size={14}
                            style={{
                                color: 'var(--color-text-tertiary)',
                                flexShrink: 0,
                                marginTop: '2px'
                            }}
                        />

                        {/* Icon */}
                        <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: 'var(--radius-md)',
                            background: `${color}20`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                        }}>
                            <Icon size={16} style={{ color }} />
                        </div>

                        {/* Content */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                                fontWeight: 600,
                                fontSize: 'var(--font-size-sm)',
                                marginBottom: '2px'
                            }}>
                                {label}
                            </div>
                            <div style={{
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-text-tertiary)',
                                lineHeight: 1.4
                            }}>
                                {description}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Help Section */}
            <div style={{
                padding: 'var(--space-3)',
                borderTop: '1px solid var(--color-border)',
                background: 'var(--color-bg-tertiary)'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 'var(--space-2)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-secondary)'
                }}>
                    <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                    <div>
                        <strong>Tip:</strong> Connect nodes by dragging from handles.
                        Campaign → Ad Set → Ad
                    </div>
                </div>
            </div>

            {/* Funnel Stage Legend */}
            <div style={{
                padding: 'var(--space-3)',
                borderTop: '1px solid var(--color-border)'
            }}>
                <div style={{
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 600,
                    color: 'var(--color-text-secondary)',
                    marginBottom: 'var(--space-2)'
                }}>
                    Funnel Stages
                </div>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--space-2)'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        fontSize: 'var(--font-size-xs)'
                    }}>
                        <span style={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '2px',
                            background: 'var(--color-info)'
                        }} />
                        <span style={{ color: 'var(--color-text-secondary)' }}>
                            TOFU - Awareness
                        </span>
                    </div>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        fontSize: 'var(--font-size-xs)'
                    }}>
                        <span style={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '2px',
                            background: 'var(--color-warning)'
                        }} />
                        <span style={{ color: 'var(--color-text-secondary)' }}>
                            MOFU - Consideration
                        </span>
                    </div>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        fontSize: 'var(--font-size-xs)'
                    }}>
                        <span style={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '2px',
                            background: 'var(--color-success)'
                        }} />
                        <span style={{ color: 'var(--color-text-secondary)' }}>
                            BOFU - Conversion
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NodePanel;
